import React from "react";
import { http } from "../Common/http";
// import { useCookies } from "react-cookie";
import { webAPIUrl } from "../Common/AppSettings";

interface UserAuthority {
  functionId: string;
  functionCode: string;
  functionName: string;
  functionGroupName: string;
  appURL: string;
  menuDispFlag: string;
  authority: string;
}
interface AuthUser {
  userId: string;
  mailAddress: string;
  userTypeCode: string;
  universityCode: string;
  sei: string;
  mei: string;
  userAuthoritys?: [UserAuthority];
  currentYear: string;
  passwordInitializationFlag: string;
  browsePastFlag: string;
  superUpdateFlag: string;
  facultyId: string;
  departmentId: string;
}
interface IAuthContext {
  isSystemOperating: boolean;
  loading: boolean;
  isAuthenticated: boolean;
  user: AuthUser;
  accessToken: string;
  removeUserLoginState: () => void;
  refreshToken: (newToken: string) => void;
}

interface SignInDataFromServer {
  userId: string;
  mailAddress: string;
  userTypeCode: string;
  universityCode: string;
  sei: string;
  mei: string;
  accessToken: string;
  userAuthoritys: [UserAuthority];
  currentYear: string;
  passwordInitializationFlag: string;
  browsePastFlag: string;
  superUpdateFlag: string;
  facultyId: string;
  departmentId: string;
}

const checkSign = async (
  accessToken: string
): Promise<SignInDataFromServer | undefined> => {
  const result = await http<SignInDataFromServer>({
    path: "/Login/isauthenticated",
    method: "get",
    accessToken,
  });
  if (result.ok && result.body) {
    return result.body;
  } else {
    return undefined;
  }
};

const checkSystemOperating = async (): Promise<Boolean> => {
  try {
    const request = new Request(`${webAPIUrl}/SystemOperatingCheck`, {
      method: "get",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const response = await fetch(request);
    if (response.ok) {
      return true;
    } else {
      return false;
    }
  } catch {
    return false;
  }
};

export const AuthContext = React.createContext<IAuthContext>({
  isSystemOperating: true,
  loading: true,
  isAuthenticated: false,
  accessToken: "",
  user: {
    userId: "",
    mailAddress: "",
    sei: "",
    mei: "",
    userTypeCode: "",
    universityCode: "",
    currentYear: "",
    passwordInitializationFlag: "",
    browsePastFlag: "",
    superUpdateFlag: "",
    facultyId: "",
    departmentId: "",
  },
  removeUserLoginState: () => {},
  refreshToken: () => {},
});

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider: React.FC = ({ children }) => {
  // const [cookies, setCookie, removeCookie] = useCookies(["auth"]);
  const [isSystemOperating, setIsSystemOperating] =
    React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [user, setUser] = React.useState<AuthUser>({
    userId: "",
    mailAddress: "",
    sei: "",
    mei: "",
    userTypeCode: "",
    universityCode: "",
    currentYear: "",
    passwordInitializationFlag: "",
    browsePastFlag: "",
    superUpdateFlag: "",
    facultyId: "",
    departmentId: "",
  });
  const [accessToken, setAccessToken] = React.useState<string>("");

  // const setContext = (
  //   isAuthenticated: boolean,
  //   accessToken: string,
  //   user: AuthUser
  // ) => {
  //   setIsAuthenticated(isAuthenticated);
  //   setUser(user);
  //   setAccessToken(accessToken);
  // };

  const removeUserLoginState = () => {
    setIsAuthenticated(false);
    setUser({
      userId: "",
      mailAddress: "",
      sei: "",
      mei: "",
      userTypeCode: "",
      universityCode: "",
      currentYear: "",
      passwordInitializationFlag: "",
      browsePastFlag: "",
      superUpdateFlag: "",
      facultyId: "",
      departmentId: "",
    });
    setAccessToken("");
  };

  const refreshToken = (newToken: string) => {
    setAccessToken(newToken);
    // setCookie("auth", newToken, { secure: true });
  };

  React.useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      const isSystemOperating = await checkSystemOperating();
      if (isSystemOperating) {
        setIsSystemOperating(true);
        // if (cookies["auth"]) {
        if (sessionStorage.getItem("auth")) {
          const result = await checkSign(sessionStorage.getItem("auth")!);
          if (result) {
            setIsAuthenticated(true);
            setAccessToken(result.accessToken);
            setUser(result);
            // setCookie("auth", result.accessToken, { secure: true });
            sessionStorage.setItem("auth", result.accessToken!);
          } else {
            // removeCookie("auth");
            sessionStorage.removeItem("auth");
            removeUserLoginState();
          }
        } else {
          removeUserLoginState();
        }
      } else {
        setIsSystemOperating(false);
        removeUserLoginState();
      }
      setLoading(false);
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isSystemOperating,
        loading,
        isAuthenticated,
        user,
        accessToken,
        removeUserLoginState,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
